/**
 * Client nutrition dashboard. Ported from the original app's NutritionDashboard.tsx —
 * same UI verbatim, with next/link → react-router Link and the api client.
 * Reads GET /api/nutrition/today.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Pencil } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { MacroTile } from '@/components/ui';
import { MEAL_SLOT_KEYS, withDefaults } from '@/lib/meals';

interface NutritionData {
  nutrition: {
    totalCalories: number;
    totalProtein: number;
    totalCarbs: number;
    totalFat: number;
    meals: Array<{
      id: string;
      meal_slot: string;
      food_name: string;
      calories: number;
      protein_g: number;
      carbs_g: number;
      fat_g: number;
    }>;
    syncedMeals?: Array<{
      meal_type: string;
      calories: number;
      protein_g: number;
      carbs_g: number;
      fat_g: number;
      items: Array<{ food_name: string | null; calories: number | null; protein_g: number | null; carbs_g: number | null; fat_g: number | null }>;
    }>;
    fromHealth?: boolean;
    source?: string;
  } | null;
  targets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    waterOz: number;
  };
  /** True when today's target is a coach-set weekday-specific override. */
  dayApplied?: boolean;
  mealLabels?: Record<string, string>;
}

/** Long weekday name for today (e.g. "Monday"), for the day-specific plan badge. */
function todayWeekday(): string {
  return new Date().toLocaleDateString(undefined, { weekday: 'long' });
}

function syncedMealLabel(type: string): string {
  switch (type) {
    case 'breakfast': return 'Breakfast';
    case 'lunch': return 'Lunch';
    case 'dinner': return 'Dinner';
    case 'snack': return 'Snacks';
    default: return 'Other';
  }
}

export default function NutritionDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<NutritionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState<Record<string, string>>(withDefaults(undefined));
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    api
      .get<NutritionData>('/api/nutrition/today')
      .then((d) => {
        setData(d);
        setLabels(withDefaults(d.mealLabels));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id]);

  async function saveLabel(slotKey: string) {
    const next = draft.trim();
    setEditing(null);
    // Optimistic: reflect immediately, then persist.
    setLabels((prev) => ({ ...prev, [slotKey]: next || prev[slotKey] }));
    try {
      const r = await api.put<{ mealLabels: Record<string, string> }>(
        '/api/nutrition/meal-labels',
        { slot_key: slotKey, label: next }
      );
      setLabels(withDefaults(r.mealLabels));
    } catch {
      /* keep optimistic value */
    }
  }

  if (loading) {
    return (
      <div className="px-4 flex flex-col gap-4">
        <div className="card animate-pulse h-40" style={{ background: 'var(--surface)' }} />
        <div className="card animate-pulse h-32" style={{ background: 'var(--surface)' }} />
      </div>
    );
  }

  const targets = data?.targets ?? {
    calories: 2000, protein: 150, carbs: 250, fat: 65, waterOz: 64,
  };
  const totals = data?.nutrition ?? {
    totalCalories: 0, totalProtein: 0, totalCarbs: 0, totalFat: 0, meals: [],
  };

  return (
    <>
      <PageHeader title="Nutrition" subtitle="Today's intake" />
      <div className="px-4 flex flex-col gap-4">
        {/* Macro tiles — match the native apps: 4 equal rounded squares,
            centered value + label + a color progress bar, shadowed. */}
        {data?.dayApplied && (
          <div className="flex mb-1">
            <span
              className="text-[10px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full"
              style={{ background: 'rgba(201,168,106,0.15)', color: 'var(--champagne)', border: '1px solid var(--gold-muted)' }}
            >
              Today&apos;s plan · {todayWeekday()}
            </span>
          </div>
        )}
        <div className="grid grid-cols-4 gap-2">
          <MacroTile value={totals.totalCalories} target={targets.calories} label="Calories" color="var(--gold)" />
          <MacroTile value={totals.totalProtein} target={targets.protein} label="Protein" color="var(--info)" />
          <MacroTile value={totals.totalCarbs} target={targets.carbs} label="Carbs" color="var(--warning)" />
          <MacroTile value={totals.totalFat} target={targets.fat} label="Fat" color="var(--error)" />
        </div>

        {/* Synced-from-Health banner (read-only totals, no in-app meals today) */}
        {data?.nutrition?.fromHealth && (
          <p className="text-xs text-center" style={{ color: 'var(--cream-muted)' }}>
            Synced from {data.nutrition.source === 'apple_health' ? 'Apple Health' : data.nutrition.source === 'health_connect' ? 'Health Connect' : 'Health'}. Log food below to track meals in-app.
          </p>
        )}

        {/* Add Food Buttons */}
        {/* Barcode scanner hidden for now — OpenFoodFacts coverage is too thin
            for US products, so scans often "can't find anything". Code + route
            (/nutrition/scan, BarcodeScan.tsx, /api/nutrition/barcode) are kept;
            re-enable by restoring the Scan Barcode link and grid-cols-3. */}
        <div className="grid grid-cols-2 gap-2">
          <Link to="/nutrition/search" className="btn-outline text-sm py-2 text-center">Search Food</Link>
          {/* <Link to="/nutrition/scan" className="btn-outline text-sm py-2 text-center">Scan Barcode</Link> */}
          <Link to="/nutrition/manual" className="btn-outline text-sm py-2 text-center">Manual Entry</Link>
        </div>

        {/* Prompt the cross-app sync path when today has no health-synced data.
            Any tracker that writes to Apple Health / Health Connect flows in
            automatically — no double-logging. (Don't name a specific app.) */}
        {!data?.nutrition?.fromHealth && (
          <p className="text-xs text-center" style={{ color: 'var(--gray-500)' }}>
            Track food in another app? Connect it to Apple Health or Health Connect and your nutrition syncs here automatically.
          </p>
        )}

        {/* Meal List — 6 renameable slots. Empty ones collapse to a thin row so
            the page isn't a wall of empty cards; tap the pencil to rename. */}
        {MEAL_SLOT_KEYS.map((slotKey) => {
          const meals = totals.meals.filter((m) => m.meal_slot === slotKey);
          const label = labels[slotKey];
          const slotCals = meals.reduce((sum, m) => sum + (m.calories || 0), 0);
          const isEditing = editing === slotKey;
          const empty = meals.length === 0;

          return (
            <div key={slotKey} className="card" style={empty ? { paddingTop: 12, paddingBottom: 12 } : undefined}>
              <div className="flex justify-between items-center gap-2">
                {isEditing ? (
                  <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => saveLabel(slotKey)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveLabel(slotKey);
                      if (e.key === 'Escape') setEditing(null);
                    }}
                    placeholder={label}
                    className="input flex-1 text-sm py-1"
                  />
                ) : (
                  <button
                    onClick={() => { setEditing(slotKey); setDraft(label); }}
                    className="flex items-center gap-1.5 group"
                  >
                    <span className="text-white font-semibold text-sm">{label}</span>
                    <Pencil size={12} style={{ color: 'var(--gray-500)' }} className="opacity-60" />
                  </button>
                )}
                <p className="text-xs whitespace-nowrap" style={{ color: 'var(--gray-400)' }}>
                  {slotCals} cal
                </p>
              </div>
              {!empty &&
                meals.map((meal) => (
                  <div
                    key={meal.id}
                    className="flex justify-between py-1.5 border-t mt-1"
                    style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                  >
                    <span className="text-sm text-white">{meal.food_name}</span>
                    <span className="text-xs" style={{ color: 'var(--gray-400)' }}>
                      {meal.calories} cal
                    </span>
                  </div>
                ))}
            </div>
          );
        })}

        {/* Synced meals (from Apple Health / Health Connect). Read-only; manual
            entry above fills anything Health doesn't provide. */}
        {(totals.syncedMeals?.length ?? 0) > 0 && (
          <>
            <p className="text-xs font-medium uppercase tracking-wider mt-1" style={{ color: 'var(--gold-muted)' }}>
              Synced from Health
            </p>
            {totals.syncedMeals!.map((g) => (
              <div key={g.meal_type} className="card">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-white font-semibold text-sm">{syncedMealLabel(g.meal_type)}</span>
                  <p className="text-xs whitespace-nowrap" style={{ color: 'var(--gray-400)' }}>{Math.round(g.calories)} cal</p>
                </div>
                {g.items.map((it, i) => (
                  <div key={i} className="flex justify-between py-1.5 border-t mt-1" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    <span className="text-sm text-white">{it.food_name || 'Item'}</span>
                    <span className="text-xs" style={{ color: 'var(--gray-400)' }}>{Math.round(it.calories || 0)} cal</span>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}

// MacroTile now lives in components/ui.tsx (shared with the Home dashboard).
//
// MacroRing (circular) and MacroBar were replaced by MacroTile to match the
// native apps. Preserved here (commented) in case the ring/bar layout is wanted
// again; remove permanently at Victoria's discretion.
//
// function MacroRing({ value, target, label, color, size }: {
//   value: number; target: number; label: string; color: string; size: number;
// }) {
//   const strokeWidth = 8;
//   const r = (size - strokeWidth) / 2;
//   const circumference = 2 * Math.PI * r;
//   const pct = Math.min(value / target, 1);
//   const offset = circumference * (1 - pct);
//   return (
//     <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
//       <svg width={size} height={size} className="-rotate-90">
//         <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--navy-light)" strokeWidth={strokeWidth} />
//         <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
//           strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="progress-ring-fill" />
//       </svg>
//       <div className="absolute text-center">
//         <p className="text-white font-bold text-lg">{Math.round(value)}</p>
//         <p className="text-[10px]" style={{ color: 'var(--gray-400)' }}>{label}</p>
//       </div>
//     </div>
//   );
// }
//
// function MacroBar({ label, current, target, unit, color }: {
//   label: string; current: number; target: number; unit: string; color: string;
// }) {
//   const pct = Math.min((current / target) * 100, 100);
//   return (
//     <div className="text-center">
//       <p className="text-white font-semibold text-sm">
//         {Math.round(current)}<span className="text-xs font-normal text-gray-400">/{target}{unit}</span>
//       </p>
//       <div className="w-full h-1.5 rounded-full mt-1.5" style={{ background: 'var(--navy)' }}>
//         <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
//       </div>
//       <p className="text-[10px] mt-1" style={{ color: 'var(--gray-500)' }}>{label}</p>
//     </div>
//   );
// }
